import { MutationTree } from 'vuex';
import { setItem as secureSetItem } from '@/services/secureStorage';
import userState from './state';

// eslint-disable-next-line no-shadow
export const enum MutationTypes {
  LOGIN_ANON = '🐱‍👤✔ Login Anonymous',
  LOGIN_ERROR = '🔐❌ Login Error',
  LOGIN_USER = 'LOGIN_USER',
  OPEN_DIALOG = 'Open Dialog',
  GET_PROGRAM = 'Get Program Doc',
  SET_ROLE = 'Set User Role',
  CLEAR_PROGRAM = 'Clear Program Doc',
  SET_PROFILE_COMPLETE = 'Set Profile Complete',
  SET_PROFILE_IMAGE = 'Set Profile Image',
  UPDATE_STUDENT_PORTFOLIO = 'Update Student Portfolio'
}

export const mutations = {
  [MutationTypes.LOGIN_ANON](state, user: typeof userState.user) {
    state.user = user;
  },
  [MutationTypes.LOGIN_USER](state, user: typeof userState.user) {
    // Preserve existing profile if the incoming user doesn't have one
    // This prevents fetchUser (which calls /get-mongo-user) from wiping out
    // profile data that was uploaded via Nexus and stored in secureStorage
    const existingProfile = state.user?.profile;
    state.user = user;
    if (user && !user.profile && existingProfile) {
      state.user.profile = existingProfile;
    }
  },
  [MutationTypes.OPEN_DIALOG](state, data) {
    state.isOpenDialog = data;
  },
  [MutationTypes.LOGIN_ERROR](state, errorCode: string) {
    state.error = errorCode;
  },
  [MutationTypes.GET_PROGRAM](state, data) {
    state.programDoc = data;
  },
  [MutationTypes.CLEAR_PROGRAM](state) {
    state.programDoc = null;
  },
  [MutationTypes.SET_ROLE](state, data) {
    state.userType = data;
  },
  [MutationTypes.SET_PROFILE_COMPLETE](state, flag: boolean) {
    state.profileComplete = flag;
  },
  [MutationTypes.SET_PROFILE_IMAGE](
    state,
    profile: { small?: string; medium?: string; large?: string } | null
  ) {
    if (state.user) {
      // Add cache-busting timestamp to URLs since S3 overwrites the same files
      const cacheBuster = `?v=${Date.now()}`;
      const profileWithCacheBuster = profile
        ? {
            small: profile.small ? `${profile.small}${cacheBuster}` : undefined,
            medium: profile.medium ? `${profile.medium}${cacheBuster}` : undefined,
            large: profile.large ? `${profile.large}${cacheBuster}` : undefined
          }
        : null;

      state.user.profile = profileWithCacheBuster;

      // Persist updated user to secureStorage so profile survives page refresh
      if (typeof window !== 'undefined') {
        secureSetItem('user', JSON.stringify(state.user));
      }
    }
  },
  /**
   * Update StudentPortfolio data in the user state
   * Used by updatePortfolio action to keep UI in sync after saves
   * Supports: school (name, district), grade, studentId, stateId
   */
  [MutationTypes.UPDATE_STUDENT_PORTFOLIO](state, payload: Record<string, any>) {
    if (!state.user) return;

    // Initialize portfolios structure if needed
    if (!state.user.portfolios) {
      state.user.portfolios = {};
    }
    if (!state.user.portfolios.Student) {
      state.user.portfolios.Student = {};
    }

    const student = state.user.portfolios.Student;

    // Handle school object (nested: { name, district })
    if (payload.school) {
      if (!student.school) {
        student.school = {};
      }
      if (payload.school.name !== undefined) {
        student.school.name = payload.school.name;
      }
      if (payload.school.district !== undefined) {
        student.school.district = payload.school.district;
      }
    }

    // Handle flat fields
    if (payload.grade !== undefined) {
      student.grade = payload.grade;
    }
    if (payload.studentId !== undefined) {
      student.studentId = payload.studentId;
    }
    if (payload.stateId !== undefined) {
      student.stateId = payload.stateId;
    }

    // Persist updated user to secureStorage so portfolio survives page refresh
    if (typeof window !== 'undefined') {
      secureSetItem('user', JSON.stringify(state.user));
    }
  }
};
